/**
 * Add identical garments back together for the bill.
 *
 * On the floor each garment is its own line, because each has its own stage,
 * trial and tailor. The customer does not care about any of that — they want
 * one row saying "Kurta × 5". So the printed bill groups lines that are the
 * same in every way that shows on it: same garment, same price, same
 * discount, same tax, same alteration flag.
 *
 * Anything that differs keeps its own row, which is the honest outcome: two
 * kurtas at different prices are two rows on the bill, as they should be.
 */

const n = (v) => Number(v || 0);

// A set is one thing to the customer: "Sherwani set", not a Sherwani and a
// Pajama on separate rows. Its parts therefore group on the set they came in
// as, whatever each part is called or costs.
const setKeyOf = (it) => it.setKey || null;

// What makes two lines the same *to a customer reading the bill*.
const billKey = (it) => (setKeyOf(it) ? `set:${setKeyOf(it)}` : [
  it.tailoringItemId ?? it.itemName,
  it.itemName,
  n(it.rate),
  n(it.discountPct),
  n(it.discountAmt),
  n(it.taxRate),
  it.gstIncl ? 1 : 0,
  it.alteration ? 1 : 0,
  // A garment someone else is collecting is a different thing on the bill.
  it.familyMemberId ?? '',
].join('|'));

/**
 * @param {Array} items  order lines, one garment each
 * @returns {Array} the same lines added together, each with `pieces` (how many
 *                  lines went in) and `sources` (those lines, in order)
 */
export function groupBillLines(items = []) {
  const out = [];
  const seen = new Map();

  for (const it of items) {
    const key = billKey(it);
    const hit = seen.get(key);
    if (!hit) {
      const row = {
        ...it,
        // A set shows under its own name, with its parts listed beneath.
        ...(setKeyOf(it) ? { itemName: it.setName || it.itemName, isSet: true } : {}),
        qty: n(it.qty) || 1,
        pieces: 1,
        sources: [it],
      };
      seen.set(key, row);
      out.push(row);
      continue;
    }
    // Quantities and money add up; everything else is already identical.
    // A set counts as one thing however many garments are in it — "Sherwani
    // set x 1", not x 2 because it has a kurta and a pajama.
    if (!hit.isSet) hit.qty += n(it.qty) || 1;
    hit.pieces += 1;
    hit.sources.push(it);
    for (const f of ['amount', 'taxableAmt', 'gstAmount', 'finalAmt', 'discountAmt']) {
      if (it[f] != null) hit[f] = n(hit[f]) + n(it[f]);
    }
  }
  return out;
}

export default groupBillLines;
